/**
 * Mock data for Manual Classification dialog states.
 * Use with ManualClassificationPreview to visualize all states.
 *
 * Covers: DocumentTable, DocumentPreviewPanel, and composed dialog states.
 */
import { DocumentCategory } from "@/constants/documents";

import { FileWithMetadata } from "../types";

// ---------------------------------------------------------------------------
// Mock files
// ---------------------------------------------------------------------------

export const MOCK_FILES: FileWithMetadata[] = [
  {
    fileId: "file-001",
    filename: "bundled_input_GENERAL_COR_61.pdf",
    classification:
      DocumentCategory.GENERAL_CORRESPONDENCE_AND_MISCELLANEOUS_DOCUMENTS,
    fileKey: "cases/CASE-1234/bundled_input_GENERAL_COR_61.pdf",
    case: { case_number: "CASE-1234" },
    startPage: 61,
    endPage: 61,
  },
  {
    fileId: "file-002",
    filename: "bundled_input_GENERAL_COR_62-64.pdf",
    classification:
      DocumentCategory.GENERAL_CORRESPONDENCE_AND_MISCELLANEOUS_DOCUMENTS,
    fileKey: "cases/CASE-1234/bundled_input_GENERAL_COR_62-64.pdf",
    case: { case_number: "CASE-1234" },
    startPage: 62,
    endPage: 64,
  },
  {
    fileId: "file-003",
    filename: "bundled_input_GENERAL_COR_114-117.pdf",
    classification:
      DocumentCategory.GENERAL_CORRESPONDENCE_AND_MISCELLANEOUS_DOCUMENTS,
    fileKey: "cases/CASE-1234/bundled_input_GENERAL_COR_114-117.pdf",
    case: { case_number: "CASE-1234" },
    startPage: 114,
    endPage: 117,
  },
  {
    fileId: "file-004",
    filename: "bundled_input_GENERAL_COR_166.pdf",
    classification:
      DocumentCategory.GENERAL_CORRESPONDENCE_AND_MISCELLANEOUS_DOCUMENTS,
    fileKey: "cases/CASE-1234/bundled_input_GENERAL_COR_166.pdf",
    case: { case_number: "CASE-1234" },
    startPage: 166,
    endPage: 166,
  },
  {
    fileId: "file-005",
    filename: "bundled_input_GENERAL_COR_185.pdf",
    classification:
      DocumentCategory.GENERAL_CORRESPONDENCE_AND_MISCELLANEOUS_DOCUMENTS,
    fileKey: "cases/CASE-1234/bundled_input_GENERAL_COR_185.pdf",
    case: { case_number: "CASE-1234" },
    startPage: 185,
    endPage: 185,
  },
  {
    fileId: "file-006",
    filename: "bundled_input_GENERAL_COR_186.pdf",
    classification:
      DocumentCategory.GENERAL_CORRESPONDENCE_AND_MISCELLANEOUS_DOCUMENTS,
    fileKey: "cases/CASE-1234/bundled_input_GENERAL_COR_186.pdf",
    case: { case_number: "CASE-1234" },
    startPage: 186,
    endPage: 186,
  },
];

// ---------------------------------------------------------------------------
// Mock files with reclassifications applied (simulates user edits)
// ---------------------------------------------------------------------------

export const MOCK_FILES_RECLASSIFIED: FileWithMetadata[] = [
  { ...MOCK_FILES[0], classification: DocumentCategory.PATHOLOGY_REPORTS },
  { ...MOCK_FILES[1], classification: DocumentCategory.CLAIMS_FORMS },
  { ...MOCK_FILES[2], classification: DocumentCategory.CLAIMS_FORMS },
  { ...MOCK_FILES[3], classification: DocumentCategory.IME_REPORTS },
  { ...MOCK_FILES[4], classification: DocumentCategory.IMC_REFERRAL_FORM },
  { ...MOCK_FILES[5], classification: DocumentCategory.IME_REPORTS },
];

// ---------------------------------------------------------------------------
// DocumentPreviewPanel mock states
// ---------------------------------------------------------------------------

export const MOCK_PREVIEW_EMPTY = {
  description: "Preview — No file selected",
  props: {
    selectedFile: null,
    selectedClassification: "",
    pageRange: "",
    fileUrl: null,
    fileType: null as "pdf" | "image" | "other" | null,
    isLoading: false,
    fileLoadError: false,
    errorMessage: null,
    isSaving: false,
    hasChanges: false,
  },
} as const;

export const MOCK_PREVIEW_LOADING = {
  description: "Preview — File loading",
  props: {
    selectedFile: MOCK_FILES[0],
    selectedClassification:
      DocumentCategory.GENERAL_CORRESPONDENCE_AND_MISCELLANEOUS_DOCUMENTS,
    pageRange: "Page 61",
    fileUrl: null,
    fileType: null as "pdf" | "image" | "other" | null,
    isLoading: true,
    fileLoadError: false,
    errorMessage: null,
    isSaving: false,
    hasChanges: false,
  },
} as const;

export const MOCK_PREVIEW_LOADED = {
  description:
    'Preview — File loaded, no file in the dialog reclassified yet (CTA reads "Keep in Miscellaneous")',
  props: {
    selectedFile: MOCK_FILES[0],
    selectedClassification:
      DocumentCategory.GENERAL_CORRESPONDENCE_AND_MISCELLANEOUS_DOCUMENTS,
    pageRange: "Page 61",
    fileUrl: "https://example.com/mock-file.pdf",
    fileType: "pdf" as const,
    isLoading: false,
    fileLoadError: false,
    errorMessage: null,
    isSaving: false,
    hasChanges: false,
  },
} as const;

export const MOCK_PREVIEW_RECLASSIFIED = {
  description:
    'Preview — Classification changed, shows original vs new (CTA reads "Save & Close")',
  props: {
    selectedFile: MOCK_FILES[4],
    selectedClassification: DocumentCategory.IMC_REFERRAL_FORM,
    pageRange: "Page 185",
    fileUrl: "https://example.com/mock-file.pdf",
    fileType: "pdf" as const,
    isLoading: false,
    fileLoadError: false,
    errorMessage: null,
    isSaving: false,
    hasChanges: true,
  },
} as const;

export const MOCK_PREVIEW_ERROR = {
  description: "Preview — File load error",
  props: {
    selectedFile: MOCK_FILES[0],
    selectedClassification:
      DocumentCategory.GENERAL_CORRESPONDENCE_AND_MISCELLANEOUS_DOCUMENTS,
    pageRange: "Page 61",
    fileUrl: null,
    fileType: null as "pdf" | "image" | "other" | null,
    isLoading: false,
    fileLoadError: true,
    errorMessage: "Failed to load file. Please check your connection.",
    isSaving: false,
    hasChanges: false,
  },
} as const;

export const MOCK_PREVIEW_SAVING = {
  description: "Preview — Saving classifications",
  props: {
    selectedFile: MOCK_FILES[4],
    selectedClassification: DocumentCategory.IMC_REFERRAL_FORM,
    pageRange: "Page 185",
    fileUrl: "https://example.com/mock-file.pdf",
    fileType: "pdf" as const,
    isLoading: false,
    fileLoadError: false,
    errorMessage: null,
    isSaving: true,
    hasChanges: true,
  },
} as const;

// All preview states in order
export const ALL_PREVIEW_STATES = [
  MOCK_PREVIEW_EMPTY,
  MOCK_PREVIEW_LOADING,
  MOCK_PREVIEW_LOADED,
  MOCK_PREVIEW_RECLASSIFIED,
  MOCK_PREVIEW_ERROR,
  MOCK_PREVIEW_SAVING,
] as const;

// ---------------------------------------------------------------------------
// DocumentTable mock states
// ---------------------------------------------------------------------------

export const MOCK_TABLE_INITIAL = {
  description: "Table — Initial state (all GENERAL_CORRESPONDENCE)",
  files: MOCK_FILES,
  selectedFilename: MOCK_FILES[0].filename,
  selectedClassification:
    DocumentCategory.GENERAL_CORRESPONDENCE_AND_MISCELLANEOUS_DOCUMENTS,
} as const;

export const MOCK_TABLE_RECLASSIFIED = {
  description: "Table — After user reclassification",
  files: MOCK_FILES_RECLASSIFIED,
  selectedFilename: MOCK_FILES_RECLASSIFIED[4].filename,
  selectedClassification: DocumentCategory.IMC_REFERRAL_FORM,
} as const;

export const MOCK_TABLE_NO_SELECTION = {
  description: "Table — No file selected",
  files: MOCK_FILES,
  selectedFilename: undefined,
  selectedClassification: undefined,
} as const;
